import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import PricingSection from '../components/PricingSection';
import ProtectedLink from '../components/ProtectedLink';
import { CONFIG } from '../utils/config';
import { useContent } from '../hooks/useContent';

const Home = () => {
  const { isDark } = useTheme();
  const location = useLocation();
  const { content, loading: contentLoading } = useContent('home');

  const RECIPIENT_OPTIONS = React.useMemo(() => {
    return content?.contact?.form?.recipients || [
      { value: 'support', label: 'Support technique', description: 'Questions produit, aide à la configuration, dépannage.', email: 'support@quikr.it' },
      { value: 'sales', label: 'Équipe commerciale', description: 'Devis, partenariats et informations tarifaires.', email: 'contact@quikr.it' }
    ];
  }, [content]);

  const demoVideoSrc = encodeURI('/video/démonstration.mp4');
  const studentAccountDemoVideoSrc = encodeURI('/video/démonstration-étudiants.mp4')
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    recipientType: 'support',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    const recipientOption =
      RECIPIENT_OPTIONS.find((option) => option.value === contactForm.recipientType) ||
      RECIPIENT_OPTIONS[0];

    const trimmedSubject = contactForm.subject?.trim();

    const payload = {
      recipients: [recipientOption.email],
      subject:
        trimmedSubject ||
        `Message via QuikR.it (${recipientOption.label})`,
      message: [
        `Nom : ${contactForm.name}`,
        `Email : ${contactForm.email}`,
        `Destinataire : ${recipientOption.label}`,
        '',
        contactForm.message.replace(/\n/g, '<br>'),
        ].join('<br>'),
    };

    try {
      const response = await fetch(`${CONFIG.BASE_URL}/send-mail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        throw new Error(
          errorBody?.error ||
            content.contact.form.sendError
        );
      }

      setSubmitStatus({
        type: 'success',
        message: content.contact.form.successMessage,
      });
      setContactForm({
        name: '',
        email: '',
        subject: '',
        message: '',
        recipientType: 'support',
      });
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message:
          error.message ||
          content.contact.form.errorFallback,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContactChange = (e) => {
    setContactForm({
      ...contactForm,
      [e.target.name]: e.target.value
    });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedRecipient = params.get('recipient');
    if (
      requestedRecipient &&
      RECIPIENT_OPTIONS.some((opt) => opt.value === requestedRecipient)
    ) {
      setContactForm((prev) =>
        prev.recipientType === requestedRecipient
          ? prev
          : { ...prev, recipientType: requestedRecipient }
      );
    }

    if (location.hash === '#contact') {
      const el = document.getElementById('contact-section');
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
    console.log("LOCATION HASH", location.hash)
    if (location.hash === '#student-offer') {
      console.log("SCROLLING TO student-offer-section")
      const el = document.getElementById('student-offer-section');
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  if (contentLoading || !content) {
    return (
      <div className="pt-16 min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  const heroFeatureIcons = [
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />,
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" />,
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
  ];

  return (
    <div className="pt-16 min-h-screen">
      <section className="relative overflow-hidden min-h-screen">
        <div
          className="absolute inset-0 bg-no-repeat chameleon-background pointer-events-none"
          style={{
            backgroundImage: `url(${content.hero.backgroundImage})`,
            backgroundPosition: 'calc(100% + 100px) center'
          }}
        ></div>

        <div className="absolute inset-0 bg-gradient-to-l from-dark-900/0 via-dark-900/70 to-dark-900 pointer-events-none"></div>


        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center min-h-[70vh] sm:min-h-[80vh]">
            <div className="space-y-6 sm:space-y-8 lg:mr-auto lg:max-w-2xl lg:transform lg:origin-left lg:scale-105">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
                <span className="block">{content.hero.title.line1}</span>
                <span className="block bg-gradient-to-r from-primary-400 to-primary-600 bg-clip-text text-transparent">
                  {content.hero.title.line2}
                </span>
              </h1>

              <p className="text-lg sm:text-xl lg:text-2xl text-gray-300 leading-relaxed">
                {content.hero.description}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {content.hero.features.map((feature, index) => (
                  <div key={index} className={`bg-dark-800/50 backdrop-blur-md p-4 sm:p-6 rounded-xl border border-dark-700/50 hover:bg-dark-800/70 transition-all duration-300 ${index === 2 ? 'sm:col-span-2 lg:col-span-1' : ''}`}>
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary-500 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {heroFeatureIcons[index]}
                      </svg>
                    </div>
                    <h3 className="text-base sm:text-lg font-semibold text-white mb-1 sm:mb-2">{feature.title}</h3>
                    <p className="text-gray-400 text-xs sm:text-sm">{feature.description}</p>
                  </div>
                ))}
              </div>
	      <div className="flex flex-wrap items-center gap-4 pt-2 sm:pt-4">
		      <div className="pt-2 sm:pt-4">
			<ProtectedLink
			  to="/dashboard"
			  className="inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-semibold text-base sm:text-lg rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
			>
			  <span>{content.hero.ctaText}</span>
			  <svg className="w-4 h-4 sm:w-5 sm:h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
			    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
			  </svg>
			</ProtectedLink>
		      </div>

		      <div className="pt-2 sm:pt-4">
			<Link
			  to="/#student-offer"
			  className="inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-semibold text-base sm:text-lg rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
			>
			  <span>{content.hero.ctaTextStudent}</span>
			  <svg className="w-4 h-4 sm:w-5 sm:h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
			    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
			  </svg>
			</Link>
		      </div>
	  	</div>

            </div>

            <div className="relative lg:pl-8">
              <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-primary-500/20 rounded-full blur-2xl animate-pulse pointer-events-none"></div>
              <div className="absolute bottom-1/4 right-1/4 w-40 h-40 bg-secondary-500/15 rounded-full blur-3xl animate-pulse animation-delay-2000 pointer-events-none"></div>
            </div>
          </div>
        </div>

        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div className="absolute top-20 right-20 w-72 h-72 bg-primary-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
          <div className="absolute bottom-20 left-20 w-72 h-72 bg-secondary-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-4000"></div>
        </div>
      </section>

      <section className={`py-20 ${
        isDark ? 'bg-dark-900' : 'bg-white'
      }`}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-10">
          <h2 className={`text-3xl md:text-4xl font-bold ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            {content.videoSection.title}
          </h2>
          <div className="relative">
            <div className={`mx-auto max-w-6xl rounded-[12px] overflow-hidden border-2 shadow-2xl ${
              isDark ? 'bg-dark-800 border-dark-600' : 'bg-white border-gray-100'
            }`}>
              <video
                className="w-full h-full aspect-video"
                controls
                playsInline
              >
                <source src={demoVideoSrc} type="video/mp4" />
                {content.contact.form.videoFallback}
              </video>
            </div>
            <div className="absolute -bottom-8 -right-8 hidden sm:block w-28 h-28 rounded-full bg-primary-500/30 blur-3xl"></div>
          </div>
        </div>
      </section>


      <section className={`py-20 ${
        isDark 
          ? 'bg-gradient-to-b from-dark-900 to-dark-800' 
          : 'bg-gradient-to-b from-gray-50 to-white'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              {content.featuresSection.title.line1}
              <span className="block bg-gradient-to-r from-primary-400 to-secondary-400 bg-clip-text text-transparent">
                {content.featuresSection.title.line2}
              </span>
            </h2>
            <p className={`max-w-4xl mx-auto text-lg sm:text-xl leading-relaxed ${
              isDark ? 'text-gray-300' : 'text-gray-600'
            }`}>
              {content.featuresSection.description}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
            <div className="space-y-6">
              {content.featuresSection.items.map((item, index) => {
                const featureIcons = [
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />,
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />,
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                ];
                const gradients = [
                  'from-primary-500 to-primary-600',
                  'from-secondary-500 to-secondary-600',
                  'from-primary-500 to-secondary-500'
                ];
                return (
                  <div key={index} className="flex items-start space-x-4">
                    <div className={`w-12 h-12 bg-gradient-to-r ${gradients[index] || gradients[0]} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {featureIcons[index]}
                      </svg>
                    </div>
                    <div>
                      <h3 className={`text-xl font-semibold mb-2 ${
                        isDark ? 'text-white' : 'text-gray-900'
                      }`}>{item.title}</h3>
                      <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                        {item.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="relative">
              <div className={`rounded-2xl p-8 border ${
                isDark
                  ? 'bg-gradient-to-br from-dark-800 to-dark-700 border-dark-600'
                  : 'bg-gradient-to-br from-white to-gray-50 border-gray-200 shadow-lg'
              }`}>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className={`rounded-lg p-4 text-center ${
                    isDark ? 'bg-dark-900' : 'bg-gray-100'
                  }`}>
                    <div className="text-2xl font-bold text-primary-400 mb-1">{content.stats.qrCodes.value}</div>
                    <div className={`text-sm ${
                      isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}>{content.stats.qrCodes.label}</div>
                  </div>
                  <div className={`rounded-lg p-4 text-center ${
                    isDark ? 'bg-dark-900' : 'bg-gray-100'
                  }`}>
                    <div className="text-2xl font-bold text-secondary-400 mb-1">{content.stats.scans.value}</div>
                    <div className={`text-sm ${
                      isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}>{content.stats.scans.label}</div>
                  </div>
                </div>
                <div className={`rounded-lg p-4 text-center ${
                  isDark ? 'bg-dark-900' : 'bg-gray-100'
                }`}>
                  <div className="text-2xl font-bold text-primary-400 mb-1">{content.stats.uptime.value}</div>
                  <div className={`text-sm ${
                    isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>{content.stats.uptime.label}</div>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full opacity-20 blur-xl"></div>
            </div>
          </div>
        </div>
      </section>

      <section className={`py-20 ${
        isDark ? 'bg-dark-800' : 'bg-white'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className={`text-3xl sm:text-4xl font-bold mb-4 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              {content.platformSection.title}
            </h2>
            <p className={`max-w-2xl mx-auto text-lg ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {content.platformSection.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {content.platformSection.cards.map((card, index) => {
              const cardIcons = [
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5h6v6H4zM14 5h6v6h-6zM4 15h6v6H4zM14 15h6v6h-6z" />,
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />,
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              ];
              const ctaLinks = ['/dashboard/qr', '/dashboard/gs1', '/dashboard/analytics'];
              const gradients = [
                'from-primary-500 to-primary-600',
                'from-secondary-500 to-secondary-600',
                'from-primary-500 to-secondary-500'
              ];
              const textColors = ['primary', 'secondary', 'primary'];

              return (
                <div key={index} className={`group rounded-2xl p-8 border hover:border-${textColors[index]}-500/50 transition-all duration-300 hover:transform hover:scale-105 ${index === 2 ? 'md:col-span-2 lg:col-span-1' : ''} ${
                  isDark
                    ? 'bg-gradient-to-br from-dark-700 to-dark-800 border-dark-600'
                    : 'bg-gradient-to-br from-white to-gray-50 border-gray-200 shadow-lg hover:shadow-xl'
                }`}>
                  <div className={`w-16 h-16 bg-gradient-to-r ${gradients[index]} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {cardIcons[index]}
                    </svg>
                  </div>
                  <h3 className={`text-xl font-bold mb-4 ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>{card.title}</h3>
                  <p className={`mb-6 ${
                    isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {card.description}
                  </p>
                  <ProtectedLink
                    to={ctaLinks[index]}
                    className={`inline-flex items-center text-${textColors[index]}-400 hover:text-${textColors[index]}-300 font-medium group-hover:translate-x-1 transition-transform`}
                  >
                    {card.ctaText}
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </ProtectedLink>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className={`py-20 ${
        isDark
          ? 'bg-gradient-to-b from-dark-800 to-dark-900'
          : 'bg-gradient-to-b from-white to-gray-100'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-primary-500/20 to-secondary-500/20 rounded-full border border-primary-500/30 mb-6">
                <svg className="w-5 h-5 text-primary-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <span className="text-primary-400 font-medium">{content.aiSection.badge}</span>
              </div>
              <h2 className={`text-3xl sm:text-4xl font-bold mb-6 ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>
                {content.aiSection.title.line1}
                <span className="block bg-gradient-to-r from-primary-400 to-secondary-400 bg-clip-text text-transparent">
                  {content.aiSection.title.line2}
                </span>
              </h2>
              <p className={`text-lg mb-8 ${
                isDark ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {content.aiSection.description}
              </p>
              <div className="space-y-4">
                {content.aiSection.features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className={`w-2 h-2 ${index % 2 === 0 ? 'bg-primary-500' : 'bg-secondary-500'} rounded-full`}></div>
                    <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>{feature}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <ProtectedLink
                  to="/dashboard/ai"
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white font-semibold rounded-xl transition-all duration-200 transform hover:scale-105"
                >
                  {content.aiSection.ctaText}
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </ProtectedLink>
              </div>
            </div>

            <div className="relative">
              <div className={`rounded-2xl p-8 border ${
                isDark
                  ? 'bg-gradient-to-br from-dark-700 to-dark-800 border-dark-600'
                  : 'bg-gradient-to-br from-white to-gray-50 border-gray-200 shadow-lg'
              }`}>
                <div className="grid grid-cols-2 gap-4">
                  {[1, 2, 3, 4].map((num, index) => {
                    const fallbackGradients = [
                      'from-primary-500 to-primary-600',
                      'from-secondary-500 to-secondary-600',
                      'from-primary-400 to-secondary-400',
                      'from-secondary-400 to-primary-400'
                    ];
                    return (
                      <div key={index} className={`rounded-xl p-2 aspect-square flex items-center justify-center ${
                        isDark ? 'bg-dark-900' : 'bg-gray-100'
                      }`}>
                        <img
                          src={`/images/qr-ai-example-${num}.png`}
                          alt={`QR Code IA - Style artistique ${num}`}
                          className="w-full h-full object-contain rounded-lg"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                          }}
                        />
                        <div className={`w-16 h-16 bg-gradient-to-r ${fallbackGradients[index]} rounded-lg opacity-80`} style={{display: 'none'}}></div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-6 text-center">
                  <p className={`text-sm ${
                    isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>{content.aiSection.examplesLabel}</p>
                </div>
              </div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-gradient-to-r from-primary-500/30 to-secondary-500/30 rounded-full blur-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      <section id="student-offer-section" className={`py-20 ${
        isDark ? 'bg-dark-900' : 'bg-white'
      }`}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-10">
          <h2 className={`text-3xl md:text-4xl font-bold ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            {content.studentPlanSection.title}
          </h2>
	  <p className={`text-lg mb-8 ${
		  isDark ? 'text-gray-300' : 'text-gray-600'
	  }`}>
	  {content.studentPlanSection.description}
	  </p>
	  <p className={`text-lg mb-8 ${
		  isDark ? 'text-gray-300' : 'text-gray-600'
	  }`}>
	  {content.studentPlanSection.schoolCallToAction}
	  </p>
          <div className="relative">
            <div className={`mx-auto max-w-6xl rounded-[12px] overflow-hidden border-2 shadow-2xl ${
              isDark ? 'bg-dark-800 border-dark-600' : 'bg-white border-gray-100'
            }`}>
              <video
                className="w-full h-full aspect-video"
                controls
                playsInline
              >
                <source src={studentAccountDemoVideoSrc} type="video/mp4" />
                {content.contact.form.videoFallback}
              </video>
            </div>
            <div className="absolute -bottom-8 -right-8 hidden sm:block w-28 h-28 rounded-full bg-primary-500/30 blur-3xl"></div>
          </div>
        </div>
      </section>



	
      <PricingSection />
      <section id="contact-section" className={`py-20 ${
        isDark
          ? 'bg-gradient-to-b from-dark-900 to-dark-800'
          : 'bg-gradient-to-b from-gray-100 to-white'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className={`text-3xl sm:text-4xl font-bold mb-4 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              {content.contact.title}
            </h2>
            <p className={`max-w-2xl mx-auto text-lg ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {content.contact.description}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-8">
              <div className={`rounded-2xl p-8 border ${
                isDark
                  ? 'bg-gradient-to-br from-dark-800 to-dark-700 border-dark-600'
                  : 'bg-gradient-to-br from-white to-gray-50 border-gray-200 shadow-lg'
              }`}>
                <h3 className={`text-xl font-bold mb-6 ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>{content.contact.otherContactTitle}</h3>

                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-secondary-500 to-secondary-600 rounded-xl flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className={`font-semibold ${
                        isDark ? 'text-white' : 'text-gray-900'
                      }`}>{content.contact.responseTime.title}</h4>
                      <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                        {content.contact.responseTime.value}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.25a9.75 9.75 0 100 19.5 9.75 9.75 0 000-19.5z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className={`font-semibold ${
                        isDark ? 'text-white' : 'text-gray-900'
                      }`}>{content.contact.supportHours.title}</h4>
                      <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                        {content.contact.supportHours.value}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className={`rounded-2xl p-8 border ${
              isDark
                ? 'bg-gradient-to-br from-dark-800 to-dark-700 border-dark-600'
                : 'bg-gradient-to-br from-white to-gray-50 border-gray-200 shadow-lg'
            }`}>
              <form onSubmit={handleContactSubmit} className="space-y-6">
                <div>
                  <span className={`block text-sm font-medium mb-2 ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {content.contact.form.recipientLabel}
                  </span>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {RECIPIENT_OPTIONS.map((option) => {
                      const isSelected = contactForm.recipientType === option.value;
                      return (
                        <label
                          key={option.value}
                          className={`relative p-4 rounded-xl border cursor-pointer transition-all duration-200 ${
                            isDark
                              ? isSelected
                                ? 'bg-dark-900 border-primary-500 text-white shadow-lg'
                                : 'bg-dark-900/60 border-dark-600 text-gray-300 hover:border-primary-500/40'
                              : isSelected
                                ? 'bg-white border-primary-500 text-gray-900 shadow-lg'
                                : 'bg-gray-50 border-gray-200 text-gray-700 hover:border-primary-500/40'
                          }`}
                        >
                          <input
                            type="radio"
                            name="recipientType"
                            value={option.value}
                            checked={isSelected}
                            onChange={handleContactChange}
                            className="sr-only"
                          />
                          <div className="space-y-1 pr-6">
                            <p className="font-semibold">{option.label}</p>
                            <p className="text-sm opacity-80">{option.description}</p>
                            {/* <p className="text-xs opacity-70">Email : {option.email}</p> */}
                          </div>
                          {isSelected && (
                            <svg
                              className="absolute top-4 right-4 w-5 h-5 text-primary-500"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      {content.contact.form.nameLabel}
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={contactForm.name}
                      onChange={handleContactChange}
                      required
                      className={`w-full px-4 py-3 rounded-lg border transition-colors duration-200 ${
                        isDark
                          ? 'bg-dark-900 border-dark-600 text-white placeholder-gray-400 focus:border-primary-500'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-primary-500'
                      } focus:outline-none focus:ring-1 focus:ring-primary-500`}
                      placeholder={content.contact.form.namePlaceholder}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      {content.contact.form.emailLabel}
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={contactForm.email}
                      onChange={handleContactChange}
                      required
                      className={`w-full px-4 py-3 rounded-lg border transition-colors duration-200 ${
                        isDark
                          ? 'bg-dark-900 border-dark-600 text-white placeholder-gray-400 focus:border-primary-500'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-primary-500'
                      } focus:outline-none focus:ring-1 focus:ring-primary-500`}
                      placeholder={content.contact.form.emailPlaceholder}
                    />
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {content.contact.form.subjectLabel}
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={contactForm.subject}
                    onChange={handleContactChange}
                    required
                    className={`w-full px-4 py-3 rounded-lg border transition-colors duration-200 ${
                      isDark
                        ? 'bg-dark-900 border-dark-600 text-white placeholder-gray-400 focus:border-primary-500'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-primary-500'
                    } focus:outline-none focus:ring-1 focus:ring-primary-500`}
                    placeholder={content.contact.form.subjectPlaceholder}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {content.contact.form.messageLabel}
                  </label>
                  <textarea
                    name="message"
                    value={contactForm.message}
                    onChange={handleContactChange}
                    required
                    rows={6}
                    className={`w-full px-4 py-3 rounded-lg border transition-colors duration-200 resize-none ${
                      isDark
                        ? 'bg-dark-900 border-dark-600 text-white placeholder-gray-400 focus:border-primary-500'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-primary-500'
                    } focus:outline-none focus:ring-1 focus:ring-primary-500`}
                    placeholder={content.contact.form.messagePlaceholder}
                  />
                </div>

                {submitStatus.message && (
                  <div className={`p-4 rounded-lg ${
                    submitStatus.type === 'success' 
                      ? 'bg-green-50 border border-green-200 text-green-600' 
                      : 'bg-red-50 border border-red-200 text-red-600'
                  }`}>
                    {submitStatus.message}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 disabled:transform-none disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center space-x-2">
                      <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span>{content.contact.form.submitting}</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-2">
                      <span>{content.contact.form.submit}</span>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                    </div>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
